import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Product, Opportunity, Column, PriorityScoringConfig, BoardMember, Resource, ResourceAllocation } from '../types';
import KanbanColumn from './KanbanColumn';
import ProductModal from './ProductModal';
import ProductCard from './ProductCard';
import OpportunityModal from './OpportunityModal';
import ColumnModal from './ColumnModal';
import Comments from './Comments';
import { usePresence } from '../hooks/usePresence';
import { 
  getProducts, 
  getColumns, 
  addProduct, 
  updateProduct, 
  deleteProduct,
  addColumn,
  updateColumn,
  deleteColumn,
  addActivity,
  subscribeToResources,
  subscribeToAllocations
} from '../firebase/firestore';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const defaultColumns: Column[] = [
  { id: '1', title: 'Ideas', color: '#8a6be8', order: 1, boardId: '' },
  { id: '2', title: 'Research', color: '#e86a94', order: 2, boardId: '' },
  { id: '3', title: 'Development', color: '#4d9a4d', order: 3, boardId: '' },
  { id: '4', title: 'Testing', color: '#f2752a', order: 4, boardId: '' },
  { id: '5', title: 'Launch', color: '#6b6b6b', order: 5, boardId: '' },
];

interface KanbanBoardProps {
  boardId: string;
  boardType?: 'product' | 'opportunity';
  scoringConfig?: PriorityScoringConfig;
  boardMembers?: BoardMember[];
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ boardId, boardType = 'product', scoringConfig, boardMembers }) => {
  const { user } = useAuth();
  const [columns, setColumns] = useState<Column[]>(defaultColumns);

  // Set up presence tracking
  usePresence(boardId);

  const [products, setProducts] = useState<Product[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const [selectedColumnId, setSelectedColumnId] = useState<string>('');
  const [activeProduct, setActiveProduct] = useState<Product | null>(null);
  
  // Column management state
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<Column | undefined>();

  // Comment state
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [selectedProductForComments, setSelectedProductForComments] = useState<Product | null>(null);

  // Resource management state
  const [resources, setResources] = useState<Resource[]>([]);
  const [allocations, setAllocations] = useState<ResourceAllocation[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Load data from Firebase on mount
  useEffect(() => {
    if (!user || !boardId) return;

    const loadData = async () => {
      try {
        const [productsData, columnsData] = await Promise.all([
          getProducts(boardId),
          getColumns(boardId)
        ]);
        
        setProducts(productsData);
        setColumns(columnsData.length > 0 ? columnsData : defaultColumns.map(col => ({ ...col, boardId })));
      } catch (error) {
        console.error('Error loading data:', error);
        // Fallback to localStorage if Firebase fails
        const savedProducts = localStorage.getItem(`satsuma_products_${boardId}`);
        const savedColumns = localStorage.getItem(`satsuma_columns_${boardId}`);
        
        if (savedProducts) {
          setProducts(JSON.parse(savedProducts).map((p: any) => ({
            ...p,
            createdAt: new Date(p.createdAt),
            updatedAt: new Date(p.updatedAt),
          })));
        }
        
        if (savedColumns) {
          setColumns(JSON.parse(savedColumns));
        } else {
          setColumns(defaultColumns.map(col => ({ ...col, boardId })));
        }
      }
    };

    loadData();

    // Set up real-time subscriptions for resources and allocations
    if (boardType === 'product') {
      const unsubscribeResources = subscribeToResources(boardId, (resourcesData) => {
        setResources(resourcesData);
      });

      const unsubscribeAllocations = subscribeToAllocations(boardId, (allocationsData) => {
        setAllocations(allocationsData);
      });

      return () => {
        unsubscribeResources();
        unsubscribeAllocations();
      };
    }
  }, [user, boardId, boardType]);

  // Save data to localStorage as backup whenever it changes
  useEffect(() => {
    if (boardId) {
      localStorage.setItem(`satsuma_products_${boardId}`, JSON.stringify(products));
    }
  }, [products, boardId]);

  useEffect(() => {
    if (boardId) {
      localStorage.setItem(`satsuma_columns_${boardId}`, JSON.stringify(columns));
    }
  }, [columns, boardId]);

  const handleAddProduct = (columnId: string) => {
    setSelectedColumnId(columnId);
    setSelectedProduct(undefined);
    setIsModalOpen(true);
  };

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setSelectedColumnId(product.columnId);
    setIsModalOpen(true);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const product = products.find(p => p.id === active.id);
    setActiveProduct(product || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveProduct(null);

    if (!over) return;

    const productId = active.id as string;
    const newColumnId = over.id as string;

    // Find the product and column
    const product = products.find(p => p.id === productId);
    const column = columns.find(c => c.id === newColumnId);

    if (!product || !column) return;

    // Update the product's column and status
    const updatedProduct = {
      ...product,
      columnId: newColumnId,
      status: column.title as any,
    };

    try {
      await updateProduct(productId, updatedProduct);
      
      // Add activity for product move
      if (user) {
        await addActivity({
          boardId,
          productId,
          type: 'product_moved',
          description: `moved "${product.name}" to ${column.title}`,
          createdBy: user.id,
          createdByEmail: user.email,
          metadata: {
            columnId: newColumnId,
          },
        });
      }
      
      setProducts(products.map(p => p.id === productId ? updatedProduct : p));
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleSaveOpportunity = async (opportunityData: Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'>) => {
    // For now, just call the product handler since they share the same structure
    await handleSaveProduct(opportunityData as any);
  };

  const handleDeleteOpportunity = async (opportunityId: string) => {
    await handleDeleteProduct(opportunityId);
  };

  const handlePatchProduct = async (productId: string, updates: Partial<Product>) => {
    if (!user) return;
    try {
      await updateProduct(productId, updates);
      const now = new Date();
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, ...updates, updatedAt: now } : p))
      );
      setSelectedProduct((prev) =>
        prev?.id === productId ? { ...prev, ...updates, updatedAt: now } : prev
      );
    } catch (error) {
      console.error('Error patching product:', error);
      throw error;
    }
  };

  const handleSaveProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) {
      console.error('No user found when trying to save product');
      return;
    }

    console.log('Saving product:', selectedProduct ? 'UPDATE' : 'CREATE', productData);

    try {
      if (selectedProduct) {
        // Update existing product
        console.log('Updating product with ID:', selectedProduct.id);
        await updateProduct(selectedProduct.id, productData);
        console.log('Product updated successfully');
        
        // Add activity for product update
        await addActivity({
          boardId,
          productId: selectedProduct.id,
          type: 'product_updated',
          description: `updated "${productData.name}"`,
          createdBy: user.id,
          createdByEmail: user.email,
        });
        
        const updatedProducts = products.map(p =>
          p.id === selectedProduct.id
            ? {
                ...p,
                ...productData,
                updatedAt: new Date(),
              }
            : p
        );
        setProducts(updatedProducts);
        console.log('Local state updated for product:', selectedProduct.id);
      } else {
        // Create new product
        console.log('Creating new product');
        const productId = await addProduct(productData, user.id);
        console.log('New product created with ID:', productId);
        
        // Add activity for product creation
        await addActivity({
          boardId,
          productId,
          type: 'product_created',
          description: `created "${productData.name}"`,
          createdBy: user.id,
          createdByEmail: user.email,
        });
        
        const newProduct: Product = {
          id: productId,
          ...productData,
          boardId,
          createdBy: user.id,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setProducts([...products, newProduct]);
        console.log('New product added to local state');
      }
      
      // Close modal after successful save
      setIsModalOpen(false);
      setSelectedProduct(undefined);
      setSelectedColumnId('');
    } catch (error) {
      console.error('Error saving product:', error);
      // Fallback to local state update
      if (selectedProduct) {
        const updatedProducts = products.map(p =>
          p.id === selectedProduct.id
            ? {
                ...p,
                ...productData,
                updatedAt: new Date(),
              }
            : p
        );
        setProducts(updatedProducts);
        console.log('Fallback: Product updated in local state only');
      } else {
        const newProduct: Product = {
          id: Date.now().toString(),
          ...productData,
          boardId,
          createdBy: user.id,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setProducts([...products, newProduct]);
        console.log('Fallback: New product added to local state only');
      }
      
      // Close modal even on error (user can see the product was saved locally)
      setIsModalOpen(false);
      setSelectedProduct(undefined);
      setSelectedColumnId('');
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!user) {
      console.error('No user found when trying to delete product');
      return;
    }

    try {
      // Get the product name before deletion for activity log
      const productToDelete = products.find(p => p.id === productId);
      const productName = productToDelete?.name || 'Unknown Product';

      // Delete from Firebase
      await deleteProduct(productId);
      console.log('Product deleted successfully');

      // Add activity for product deletion
      await addActivity({
        boardId,
        productId,
        type: 'product_updated', // Using updated since we don't have a delete type
        description: `deleted "${productName}"`,
        createdBy: user.id,
        createdByEmail: user.email,
      });

      // Update local state
      setProducts(products.filter(p => p.id !== productId));
      console.log('Product removed from local state');
    } catch (error) {
      console.error('Error deleting product:', error);
      throw new Error('Failed to delete product');
    }
  };

  // Comment handlers
  const handleCommentClick = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProductForComments(product);
    setIsCommentsOpen(true);
  };

  const handleCommentsClose = () => {
    setIsCommentsOpen(false);
    setSelectedProductForComments(null);
  };

  // Remove logout functionality as it's now in the header
  // const handleLogout = () => {
  //   logout();
  // };

  const getProductsForColumn = (columnId: string) => {
    const columnProducts = products.filter(product => product.columnId === columnId);
    // Sort products by priority score (highest first)
    return columnProducts.sort((a, b) => {
      const scoreA = a.priorityScore || 0;
      const scoreB = b.priorityScore || 0;
      return scoreB - scoreA; // Descending order (highest first)
    });
  };

  // Column management functions
  const handleAddColumn = () => {
    setSelectedColumn(undefined);
    setIsColumnModalOpen(true);
  };

  const handleEditColumn = (column: Column) => {
    setSelectedColumn(column);
    setIsColumnModalOpen(true);
  };

  const handleSaveColumn = async (columnData: Omit<Column, 'id'>) => {
    try {
      const columnId = await addColumn(columnData, boardId);
      const newColumn: Column = {
        id: columnId,
        ...columnData,
      };
      setColumns([...columns, newColumn]);
    } catch (error) {
      console.error('Error adding column:', error);
      // Fallback to local state
      const newColumn: Column = {
        id: Date.now().toString(),
        ...columnData,
      };
      setColumns([...columns, newColumn]);
    }
  };

  const handleUpdateColumn = async (columnId: string, updates: Partial<Column>) => {
    try {
      await updateColumn(columnId, updates);
      setColumns(columns.map(col => 
        col.id === columnId ? { ...col, ...updates } : col
      ));
    } catch (error) {
      console.error('Error updating column:', error);
      // Fallback to local state
      setColumns(columns.map(col => 
        col.id === columnId ? { ...col, ...updates } : col
      ));
    }
  };

  const handleDeleteColumn = async (columnId: string) => {
    try {
      await deleteColumn(columnId);
      
      // Move all products from deleted column to the first available column
      const remainingColumns = columns.filter(col => col.id !== columnId);
      if (remainingColumns.length > 0) {
        const firstColumnId = remainingColumns[0].id;
        const productsToMove = products.filter(product => product.columnId === columnId);
        
        // Update products in Firebase
        for (const product of productsToMove) {
          try {
            await updateProduct(product.id, { columnId: firstColumnId });
          } catch (error) {
            console.error('Error moving product:', error);
          }
        }
        
        // Update local state
        setProducts(products.map(product => 
          product.columnId === columnId 
            ? { ...product, columnId: firstColumnId }
            : product
        ));
      }
      
      setColumns(remainingColumns);
    } catch (error) {
      console.error('Error deleting column:', error);
      // Fallback to local state
      const remainingColumns = columns.filter(col => col.id !== columnId);
      if (remainingColumns.length > 0) {
        const firstColumnId = remainingColumns[0].id;
        setProducts(products.map(product => 
          product.columnId === columnId 
            ? { ...product, columnId: firstColumnId }
            : product
        ));
      }
      setColumns(remainingColumns);
    }
  };

  // Calculate dynamic column width based on number of columns
  const getColumnWidth = () => {
    const columnCount = columns.length;
    if (columnCount <= 3) return 'w-96'; // 384px
    if (columnCount <= 4) return 'w-80'; // 320px
    if (columnCount <= 5) return 'w-72'; // 288px
    return 'w-64'; // 256px for 6+ columns
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-heian-50 via-washi-50 to-sakura-50">
      {/* Board */}
      <div className="max-w-full mx-auto px-6 py-8">
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-start space-x-6 overflow-x-auto pb-6"
          >
            {columns.map((column) => (
              <SortableContext
                key={column.id}
                items={getProductsForColumn(column.id).map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={`${getColumnWidth()} flex-shrink-0`}>
                  <KanbanColumn
                    column={column}
                    products={getProductsForColumn(column.id)}
                    boardType={boardType}
                    onAddProduct={handleAddProduct}
                    onProductClick={handleProductClick}
                    onCommentClick={handleCommentClick}
                    onEditColumn={handleEditColumn}
                    onDeleteColumn={handleDeleteColumn}
                    scoringConfig={scoringConfig}
                  />
                </div>
              </SortableContext>
            ))}
            
            {/* Add Column Button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`${getColumnWidth()} flex-shrink-0`}
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddColumn}
                className="w-full h-32 border-2 border-dashed border-white/40 rounded-xl text-gray-500 hover:text-gray-700 hover:border-white/60 transition-all duration-200 flex flex-col items-center justify-center space-y-2 group"
              >
                <Plus size={24} className="group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium">Add Column</span>
              </motion.button>
            </motion.div>
          </motion.div>

          <DragOverlay>
            {activeProduct ? (
              <ProductCard
                id={activeProduct.id}
                product={activeProduct}
                onClick={() => {}}
                onCommentClick={() => {}}
                scoringConfig={scoringConfig}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Product/Opportunity Modal */}
      {user && boardType === 'product' && (
        <ProductModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveProduct}
          onPatchProduct={handlePatchProduct}
          onDelete={handleDeleteProduct}
          product={selectedProduct}
          columnId={selectedColumnId}
          boardId={boardId}
          userId={user.id}
          boardMembers={boardMembers}
          resources={resources}
          allocations={allocations}
        />
      )}
      {user && boardType === 'opportunity' && (
        <OpportunityModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveOpportunity}
          onDelete={handleDeleteOpportunity}
          opportunity={selectedProduct as any}
          columnId={selectedColumnId}
          boardId={boardId}
          userId={user.id}
        />
      )}

      {/* Column Modal */}
      <ColumnModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        onSave={handleSaveColumn}
        onUpdate={handleUpdateColumn}
        onDelete={handleDeleteColumn}
        column={selectedColumn}
        boardId={boardId}
        existingColumns={columns}
      />

      {/* Comments Modal */}
      {selectedProductForComments && (
        <Comments
          productId={selectedProductForComments.id}
          boardId={boardId}
          isOpen={isCommentsOpen}
          onClose={handleCommentsClose}
          boardMembers={boardMembers}
        />
      )}


    </div>
  );
};

export default KanbanBoard;
